import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, Gem, PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { useSitePhotos } from "../lib/sitePhotos";

export const OCCASIONS = [
  {
    id: "corporate",
    label: "Corporate Catering",
    icon: Briefcase,
    photo: "chefPlating",
    alt: "HiPlate team serving a corporate halal lunch line",
    desc: "Bring Eat HiPlate to your next meeting, team lunch, or company celebration. Contact us to discuss catering options for your event.",
  },
  {
    id: "wedding",
    label: "Wedding Catering",
    icon: Gem,
    photo: "weddingTable",
    alt: "Elegant halal wedding table setting",
    desc: "Celebrate your special day with Eat HiPlate. Share your wedding plans, guest count, and food preferences so we can discuss catering for your celebration.",
  },
  {
    id: "parties",
    label: "Private Parties",
    icon: PartyPopper,
    photo: "buffetLine",
    alt: "Halal party buffet spread with grilled chicken, falafel and salads",
    desc: "From birthdays and anniversaries to family gatherings, bring Eat HiPlate to your next celebration. Contact us to discuss food for your occasion.",
  },
];

export const INQUIRY_ANCHOR = "catering-inquiry";

// Tiny event bus so package cards (and anything else) can open the dialog / preselect the estimator style.
const OPEN_EVT = "hiplate:open-occasion";
const STYLE_EVT = "hiplate:select-style";
export const openOccasion = (occasionId, packageId, img) =>
  window.dispatchEvent(new CustomEvent(OPEN_EVT, { detail: { occasionId, packageId, img } }));
export const onSelectStyle = (handler) => {
  const fn = (e) => handler(e.detail);
  window.addEventListener(STYLE_EVT, fn);
  return () => window.removeEventListener(STYLE_EVT, fn);
};

export const CateringOccasions = ({ light = true }) => {
  const [open, setOpen] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const IMG = useSitePhotos();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const fn = (e) => {
      const occ = OCCASIONS.find((o) => o.id === e.detail.occasionId);
      if (occ) { setPackageId(e.detail.packageId || null); setOpen(e.detail.img ? { ...occ, img: e.detail.img } : occ); }
    };
    window.addEventListener(OPEN_EVT, fn);
    return () => window.removeEventListener(OPEN_EVT, fn);
  }, []);

  useEffect(() => {
    if (window.location.hash === `#${INQUIRY_ANCHOR}`) {
      setTimeout(() => document.getElementById(INQUIRY_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [pathname]);

  const requestQuote = () => {
    setOpen(null);
    if (packageId) window.dispatchEvent(new CustomEvent(STYLE_EVT, { detail: packageId }));
    if (pathname === "/catering") {
      document.getElementById(INQUIRY_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/catering#${INQUIRY_ANCHOR}`);
    }
  };

  const chip = light
    ? "border-parchment/20 text-parchment/85 hover:border-saffron hover:bg-parchment/10 hover:text-parchment"
    : "border-inkline text-ink/80 hover:border-spice hover:bg-spice/5 hover:text-ink";

  return (
    <>
      <div className="mt-7 flex flex-wrap gap-3" data-testid="catering-occasions">
        {OCCASIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`occasion-card-${o.id}`}
            onClick={() => { setPackageId(null); setOpen(o); }}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${chip}`}
          >
            <o.icon size={15} className={light ? "text-saffron" : "text-spice"} /> {o.label}
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg overflow-hidden rounded-3xl border-inkline bg-parchment p-0 text-ink" data-testid="occasion-dialog">
          {open && (
            <>
              <img src={open.img || IMG[open.photo]} alt={open.alt} className="h-52 w-full object-cover sm:h-64" data-testid="occasion-dialog-image" />
              <div className="p-7 sm:p-8">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-spice">
                  <open.icon size={13} /> Catering
                </div>
                <DialogTitle className="mt-2 font-display text-2xl font-semibold text-ink sm:text-3xl" data-testid="occasion-dialog-title">
                  {open.label}
                </DialogTitle>
                <DialogDescription className="mt-3 text-sm leading-relaxed text-ink/75 sm:text-base" data-testid="occasion-dialog-desc">
                  {open.desc}
                </DialogDescription>
                <button
                  type="button"
                  onClick={requestQuote}
                  data-testid="occasion-quote-btn"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-spice px-7 py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink sm:w-auto"
                >
                  Request a Catering Quote <ArrowRight size={16} />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
